import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Hotel, Building, MapPin, User, FileText, Bed } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { addProperty } from "@/api/property";

// Define the property schema with validation
const propertySchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  location: z.string().min(5, { message: "Please enter a valid location." }),
  abn: z
    .string()
    .regex(/^\d{11}$/, { message: "ABN must be 11 digits." }),
  managerName: z.string().min(2, { message: "Manager name is required." }),
  rooms: z
    .number()
    .min(1, { message: "Number of rooms must be at least 1." })
    .optional(),
});

// Type for our form values
type PropertyFormValues = z.infer<typeof propertySchema>;

const AddProperty = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with default values
  const form = useForm<PropertyFormValues>({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      name: "",
      location: "",
      abn: "",
      managerName: "",
      rooms: 0,
    },
  });

  // Handle form submission
  const onSubmit = async (data: PropertyFormValues) => {
    setIsSubmitting(true);

    try {
      const result = await addProperty({
        name: data.name,
        description: data.location,
        abn: data.abn,
        rooms: data.rooms || 0,
        enabled: false,
        myob: false,
        rms: false,
      });
      console.log(result);

      if (result.status === 200) {
        toast({
          title: "Property Added",
          description: `${data.name} has been successfully added.`,
        });

        navigate("/properties");
      } else {
        throw new Error("Failed to add property");
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add property. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <Hotel className="mr-2 h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Add New Property</h1>
      </div>

      <div className="max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-sm border">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <Building className="mr-2 h-4 w-4" />
                    Property Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Grand Hotel" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the official name of the property.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4" />
                    Location
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main St, Sydney, NSW" {...field} />
                  </FormControl>
                  <FormDescription>
                    Full address of the property.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="abn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <FileText className="mr-2 h-4 w-4" />
                    ABN
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="12345678901" {...field} />
                  </FormControl>
                  <FormDescription>
                    Australian Business Number (11 digits).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="managerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <User className="mr-2 h-4 w-4" />
                    Manager Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="John Smith" {...field} />
                  </FormControl>
                  <FormDescription>
                    Name of the property manager.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rooms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    <Bed className="mr-2 h-4 w-4" />
                    Number of Rooms
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="10"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter the total number of rooms in the property.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/properties")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Saving...
                  </>
                ) : (
                  "Add Property"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default AddProperty;
